import React from "react";
import { motion } from "framer-motion";

/**
 * Elegant, on-brand image placeholder slot.
 * Uses soft baby-blue gradient + glassmorphism per design guidelines.
 * Renders an actual <img> only when `src` is provided (user will add later).
 */
export const ImagePlaceholder = ({
  src,
  alt = "",
  ratio = "aspect-[4/5]",
  label = "STXP · PHOTO",
  rounded = "rounded-[24px]",
  className = "",
  imgClassName = "",
  hover = true,
  testId,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={`relative overflow-hidden ${rounded} ${ratio} bg-gradient-to-br from-[#E8F4FF]/60 via-white/70 to-[#F4FAFF]/60 border border-[#C6E2FF]/40 shadow-[0_10px_40px_rgba(198,226,255,0.25)] group ${className}`}
      data-testid={testId}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className={`w-full h-full object-cover ${imgClassName} ${hover ? "transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]" : ""}`}
        />
      ) : (
        <>
          {/* Decorative concentric rings */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[62%] aspect-square rounded-full border border-[#C6E2FF]/40" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[42%] aspect-square rounded-full border border-[#C6E2FF]/50" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-[22%] aspect-square rounded-full bg-white/50 backdrop-blur-sm border border-white/70" />
          </div>
          {/* Corner marks */}
          <div className="absolute top-4 left-4 w-3 h-3 border-t border-l border-[#A8D2FA]/70" />
          <div className="absolute top-4 right-4 w-3 h-3 border-t border-r border-[#A8D2FA]/70" />
          <div className="absolute bottom-4 left-4 w-3 h-3 border-b border-l border-[#A8D2FA]/70" />
          <div className="absolute bottom-4 right-4 w-3 h-3 border-b border-r border-[#A8D2FA]/70" />
          {/* Label */}
          <div className="absolute inset-0 flex items-end justify-center pb-6">
            <span className="font-manrope text-[10px] tracking-[0.35em] uppercase text-[#A8D2FA]/90">
              {label}
            </span>
          </div>
        </>
      )}
      {/* Soft sheen on hover */}
      {hover && (
        <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-tr from-white/0 via-white/20 to-white/0" />
      )}
    </motion.div>
  );
};

export default ImagePlaceholder;
